import { Link } from 'react-router-dom'
import type { PipelineStageDto, VenuePipelineDto } from '@lib/dto/pipeline.dto'
import { FUNNEL_TARGET_LABELS, isFunnelPriced, type FunnelTarget } from '@lib/enums/funnel-stage.enum'
import { Change, changeOf, StatCost, StatTile, StatValue, Trend } from '@/components/stats/StatsPieces'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useHoneybookStatus } from '@/hooks/useHoneyBook'
import { useVenuePipeline } from '@/hooks/usePipeline'
import { formatAdPeriod, formatCount, formatMicros } from '@/lib/adFormat'
import { instantLabel } from '@/lib/instantLabel'

/** The steps the panel shows, each only where something at the venue records it. */
const SHOWN: readonly FunnelTarget[] = ['inquiry', 'tour_scheduled', 'toured', 'booked']

function StageTile({ stage, currency }: { stage: PipelineStageDto; currency: string | null }) {
  const label = FUNNEL_TARGET_LABELS[stage.target]
  return (
    <StatTile label={label} stillMoving={stage.stillMoving}>
      <StatValue>{formatCount(stage.value ?? 0)}</StatValue>
      <Change
        change={changeOf(stage.value, stage.previous)}
        against={stage.previous === null ? null : formatCount(stage.previous)}
        sameAge={stage.sameAge}
      />
      <Trend points={stage.trend} format={formatCount} label={label} />
      {currency === null || !isFunnelPriced(stage.target) || (stage.value ?? 0) === 0 ? null : (
        <StatCost>{stage.costPerMicros === null ? 'Too few to price' : `${formatMicros(stage.costPerMicros, currency)} each`}</StatCost>
      )}
    </StatTile>
  )
}

function Tiles({ data }: { data: VenuePipelineDto }) {
  const stages = SHOWN.flatMap((target) => data.stages.filter((s) => s.target === target && s.tracked))
  const currency = data.spend?.currencyCode ?? null
  if (data.window === null) return <p className="m-0 text-sm text-ink-muted">No couples have come in yet.</p>
  return (
    <div className="space-y-3">
      <p className="m-0 text-small text-ink-muted">
        Couples who inquired {formatAdPeriod(data.window.start, data.window.end)}, compared with{' '}
        {formatAdPeriod(data.window.compareStart, data.window.compareEnd)}
      </p>
      <div className="flex flex-wrap gap-3">
        {stages.map((stage) => (
          <StageTile key={stage.target} stage={stage} currency={currency} />
        ))}
      </div>
    </div>
  )
}

/** The panel's numbers, once HoneyBook has been read: the pipeline is worked out only then. */
function Numbers({ venueId }: { venueId: string }) {
  const pipeline = useVenuePipeline(venueId, { view: 'cohort' })
  if (pipeline.isPending) return <DetailSkeleton />
  if (pipeline.isError) return <ErrorState message={requestErrorMessage(pipeline.error)} onRetry={() => void pipeline.refetch()} />
  return <Tiles data={pipeline.data} />
}

/**
 * A HoneyBook venue's pipeline on its dashboard (docs/honeybook.plan.md, 3.6):
 * the latest period's inquiries, tours and bookings, each against the period
 * before, from the same numbers as its Pipeline page. Nothing at all for a venue
 * without HoneyBook.
 */
export function PipelinePanel({ venueId }: { venueId: string }) {
  const status = useHoneybookStatus(venueId, true)
  const data = status.data
  if (status.isPending || status.isError || data === undefined || data.state === 'none') return null

  return (
    <section aria-labelledby="pipeline-heading" className="space-y-5 rounded-xl border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h2 id="pipeline-heading" className="m-0 text-heading font-semibold text-ink">
            Pipeline
          </h2>
          <p className="m-0 text-small text-ink-muted">
            {data.readAt === null ? 'From HoneyBook' : `From HoneyBook · updated ${instantLabel(data.readAt)}`}
          </p>
        </div>
        <Link to={`/venues/${venueId}/pipeline`} className="text-sm font-medium text-accent-ink hover:underline">
          Open Pipeline
        </Link>
      </div>
      {data.state === 'first_read' ? (
        <p className="m-0 text-sm text-ink-muted">Reading HoneyBook for the first time…</p>
      ) : (
        <Numbers venueId={venueId} />
      )}
    </section>
  )
}
